import React, { useState, useEffect, useRef, useCallback } from "react";
import { Offcanvas } from "react-bootstrap";
import PatientOutDetails from "../ClinicModal/PatientOutDetails";
import PatientStepper from "./PatientStepper";
import OutPatientDetails from "../ClinicModal/OutPatientDetails";
import { useGetOutPatientDetailsQuery } from "../../State/Api/GetoutpatientApiSlice";
import { X } from "lucide-react";
import {
  User,
  Calendar,
  FileText,
  Pill,
  ClipboardList,
  CreditCard,
} from 'lucide-react';
import dayjs from "dayjs";

const drawerStyles = `
  :root {
    --color-secondary: #10b981;
    --color-secondary-mid: #059669;
    --color-secondary-dark: #047857;
    --color-dark-light: #4b5563;
    --color-dark: #374151;
    --color-dark-deep: #1f2937;
    --color-orange: #f97316;
    --color-orange-light: #fb923c;
  }

  .patient-drawer-container {
    height: 100vh;
    display: flex;
    flex-direction: column;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  }

  .patient-drawer-header {
    position: sticky;
    top: 0;
    z-index: 100;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    border-bottom: 1px solid #e2e8f0;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    backdrop-filter: blur(10px);
  }

  .patient-drawer-body {
    flex: 1;
    overflow-y: auto;
    padding: 0;
    background: #f8fafc;
    scroll-behavior: smooth;
  }

  .drawer-close-btn {
    position: fixed;
    top: 20px;
    right: 20px;
    background: rgba(255,255,255,0.95);
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 101;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }

  .drawer-close-btn:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #dc2626;
    transform: scale(1.05);
  }

  .patient-drawer-body::-webkit-scrollbar {
    width: 6px;
  }

  .patient-drawer-body::-webkit-scrollbar-track {
    background: #f1f5f9;
  }

  .patient-drawer-body::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, var(--color-secondary), var(--color-secondary-mid));
    border-radius: 3px;
  }
`;

const OutPatientDrawer = ({ activeStep, setActiveStep, show, onClose, placement = "end", ids }) => {
  const {data, isSuccess, isLoading, isFetching} = useGetOutPatientDetailsQuery({patientUserUniqueID : ids?.patientUserUniqueID, appointmentId : ids?.appointmentId});

  const patientInfo = data?.patientInfo || {};
const prescriptions = data?.prescriptions || [];
const labReports = data?.labReports || [];

const stepData = [
    {
      id: 'patient-details',
      label: 'Patient',
      icon: <User size={18} />,
      detailTitle: 'Patient Information',
      detailValue: `${patientInfo?.patientName || "N/A"} - ${patientInfo?.age || ""} yrs, ${patientInfo?.gender || ""}`,
      time: patientInfo?.registeredOn ? `Registered ${dayjs(patientInfo.registeredOn).format("DD-MMM-YYYY")}` : "No registration date",
    },
    {
      id: 'appointment-info',
      label: 'Appointment',
      icon: <Calendar size={18} />,
      detailTitle: 'Consultation',
      detailValue: `${patientInfo?.doctorName || "N/A"} - ${patientInfo?.department || ""}`,
      time: patientInfo?.appointmentDate ? `${dayjs(patientInfo.appointmentDate).format("DD-MMM-YYYY")} at ${patientInfo?.time || ""}` : "No appointment date",
    },
    {
      id: 'prescriptions',
      label: 'Prescriptions',
      icon: <Pill size={18} />,
      detailTitle: 'Current Medications',
      detailValue: prescriptions?.length
        ? prescriptions.map(p => p.medicationName).join(", ")
        : "No prescriptions",
      time: prescriptions?.length
        ? `Prescribed ${dayjs(patientInfo?.appointmentDate).format("DD-MMM-YYYY")}`
        : "No prescription date",
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: <FileText size={18} />,
      detailTitle: 'Lab Results',
      detailValue: labReports?.length
        ? labReports.map(r => r.testName).join(", ")
        : "No reports",
      time: labReports?.length
        ? `Latest: ${dayjs(
            labReports.reduce((latest, r) => {
              const date = dayjs(r.reportDate);
              return date.isAfter(latest) ? date : latest;
            }, dayjs(labReports[0]?.reportDate))
          ).format("DD-MMM-YYYY")}`
        : "No report date",
    },
    {
      id: 'billing-summary',
      label: 'Billing',
      icon: <CreditCard size={18} />,
      detailTitle: 'Payment Status',
      detailValue: `₹${patientInfo?.totalBill || 0} - ${patientInfo?.paymentStatus || "Unpaid"}`,
      time: patientInfo?.appointmentDate
        ? `Completed ${dayjs(patientInfo.appointmentDate).format("DD-MMM-YYYY")}`
        : "No billing date",
    },
    {
      id: 'discharge-summary',
      label: 'Discharge',
      icon: <ClipboardList size={18} />,
      detailTitle: 'Discharge Summary',
      detailValue: patientInfo?.dischargeSummary || "No summary available",
      time: patientInfo?.appointmentDate
        ? `Summary updated ${dayjs(patientInfo.appointmentDate).format("DD-MMM-YYYY")}`
        : "No summary date",
    },
  ];



  const [activeSection, setActiveSection] = useState('patient-details');
  const [scrollProgress, setScrollProgress] = useState({});
  const scrollContainerRef = useRef(null);
  const isUserScrollingRef = useRef(false);
    const drawerRef = useRef(null);
  const stepRefs = useRef([]);



  // Combined observer for better synchronization


  // Enhanced scroll monitoring
  useEffect(() => {
    const drawer = drawerRef.current;
    if (!drawer) return;

    const sections = Array.from(drawer.querySelectorAll(".details-card"));
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {
          const id = visible[0].target.id;
          const index = sections.findIndex((section) => section.id === id);
          if (index !== -1) {
            setActiveStep(index);
          }
        }
      },
      {
        root: drawer,
        rootMargin: "0px 0px -50% 0px", // triggers when section reaches middle
        threshold: 0.3,
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, [show, setActiveStep]);


  // Handle stepper clicks with stepData now available
 // Enhanced stepper click handler in OutPatientDrawer
const handleStepperClick = (sectionId) => {
 
  // Prevent scroll-based updates during user navigation
  isUserScrollingRef.current = true;
  
  // Enhanced progress management for cleaner state transitions
  const newProgress = {};
  const clickedIndex = stepData.findIndex(step => step.id === sectionId);
  
  stepData.forEach((step, index) => {
    if (index > clickedIndex) {
      // Clear all future steps completely
      newProgress[step.id] = 0;
    } else if (index < clickedIndex) {
      // Mark previous steps as completed only if they're sequential
      newProgress[step.id] = 0.85; // High completion value
    } else {
      // Current step gets good active progress
      newProgress[step.id] = 0.6;
    }
  });
  
  // Update state immediately
  setActiveSection(sectionId);
  setScrollProgress(newProgress);
  
  // Scroll to section
  const element = document.getElementById(sectionId);
  if (element && scrollContainerRef.current) {
    element.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest'
    });
    
    // Re-enable scroll detection after scroll animation
    setTimeout(() => {
      isUserScrollingRef.current = false;
    }, 1000);
  }
};


  return (
    <>
      <style>{drawerStyles}</style>
      <Offcanvas 
        show={show} 
        onHide={onClose} 
        placement={placement}
        backdrop="static"
        style={{ width: "1140px", maxWidth: "100%" }}
        className="offcanvas-xxl"
      >
        <div className="patient-drawer-container">
          <button className="drawer-close-btn" onClick={onClose}>
            <X size={24} />
          </button>
          
          {/* Sticky Header with Profile & Stepper */}
          <div className="patient-drawer-header">
            <PatientOutDetails patient={data} />
            <PatientStepper 
              stepData={stepData}
              activeSection={activeSection}
              scrollProgress={scrollProgress}
              onStepClick={handleStepperClick}
            />
          </div>

          {/* Scrollable Content */}
          <div className="patient-drawer-body" ref={scrollContainerRef}>
            <OutPatientDetails patient={data} />
          </div>
        </div>
      </Offcanvas>
    </>
  );
};

export default OutPatientDrawer;
